<script setup>
import { computed, ref, onMounted, onBeforeUnmount, watch } from 'vue'
import { onBeforeRouteLeave } from 'vue-router'
import AppCard from './ui/AppCard.vue'
import LifecycleStatusBadge from './LifecycleStatusBadge.vue'
import { useLotteryStore } from '../stores/lottery.js'
import TicketBatchModal from './TicketBatchModal.vue'
import { getLifecycleStatusMeta } from '../utils/adminUi.js'
import { groupWinnersForDisplay, normalizeDrawHistory, parseDrawExecutionError } from '../utils/lotteryUi.js'

const props = defineProps({
  lotteryId: {
    type: String,
    required: true,
  },
  assignments: {
    type: Array,
    default: () => [],
  },
})

const lotteryStore = useLotteryStore()
const drawResult = ref(null)
const isDrawing = ref(false)
const drawError = ref('')
const isMobile = ref(false)
let mobileResizeHandler = null
let beforeUnloadHandler = null

// Modal-state
const modalBatch = ref(null)
const modalTitle = ref('')
const modalHighlightTicket = ref(null)
const modalUsername = ref('')
const modalLoading = ref(false)

const syncIsMobile = () => {
  if (typeof window === 'undefined') return
  isMobile.value = window.innerWidth <= 768
}

onMounted(() => {
  syncIsMobile()
  mobileResizeHandler = () => syncIsMobile()
  window.addEventListener('resize', mobileResizeHandler)
  lotteryStore.fetchDrawHistory(props.lotteryId)
})

onBeforeUnmount(() => {
  if (mobileResizeHandler) window.removeEventListener('resize', mobileResizeHandler)
  if (beforeUnloadHandler) {
    window.removeEventListener('beforeunload', beforeUnloadHandler)
    beforeUnloadHandler = null
  }
})

const usernameByUserId = computed(() => {
  const map = new Map()
  for (const assignment of props.assignments || []) {
    const id = assignment?.user_id
    if (id === undefined || id === null) continue
    if (assignment?.username) map.set(String(id), assignment.username)
  }
  return map
})

const formatUserLabel = (userId) => {
  const idText = String(userId ?? '')
  const username = usernameByUserId.value.get(idText)
  return username ? `${username} (#${idText})` : `#${idText}`
}

const drawLockActive = computed(() => lotteryStore.isDrawLocked(props.lotteryId))
const drawBlocked = computed(() => isDrawing.value || drawLockActive.value)

const drawHistoryRows = computed(() => {
  return normalizeDrawHistory(lotteryStore.drawHistory).map((entry) => {
    const firstWinner = Array.isArray(entry.winners) ? entry.winners[0] : null
    return {
      key: entry.id,
      ticketNumber: firstWinner?.ticket_number ?? firstWinner?.winning_number ?? null,
      userId: firstWinner?.user_id ?? null,
      ticketBatchId: firstWinner?.ticket_batch_id ?? null,
      createdAt: entry.executedAt,
      drawRecord: firstWinner,
    }
  })
})

const winnerRows = computed(() => {
  const payload = drawResult.value
  if (!payload) return []

  const winners = Array.isArray(payload?.winners)
    ? payload.winners
    : payload?.winning_number || payload?.ticket_number || payload?.user_id
      ? [payload]
      : []

  return groupWinnersForDisplay(winners)
})

const winnerLifecycleMeta = (winner) => {
  if (!winner || typeof winner !== 'object') return getLifecycleStatusMeta(false)
  const deleted = winner.record?.is_deleted === true || winner.record?.user_deleted === true
  return getLifecycleStatusMeta(deleted)
}

const formatWinnerLabel = (winner) => {
  if (winner?.username) return winner.username
  if (winner?.userId !== null && winner?.userId !== undefined) return formatUserLabel(winner.userId)
  return 'Unknown winner'
}

const formatTicketNumber = (ticketNumber) => {
  const parsed = Number(ticketNumber)
  return Number.isInteger(parsed) && parsed >= 0 ? String(parsed) : 'Missing'
}

const preventUnloadWhileDrawing = (event) => {
  if (!drawBlocked.value) return
  event.preventDefault()
  event.returnValue = 'Draw execution is still in progress.'
}

onBeforeRouteLeave(() => {
  if (!drawBlocked.value) return true
  return window.confirm('Draw execution is in progress. Leaving now can hide the final result. Leave anyway?')
})

watch(drawBlocked, (blocked) => {
  if (typeof window === 'undefined') return

  if (blocked && !beforeUnloadHandler) {
    beforeUnloadHandler = preventUnloadWhileDrawing
    window.addEventListener('beforeunload', beforeUnloadHandler)
    return
  }

  if (!blocked && beforeUnloadHandler) {
    window.removeEventListener('beforeunload', beforeUnloadHandler)
    beforeUnloadHandler = null
  }
})

const handleDraw = async () => {
  if (drawBlocked.value) return

  isDrawing.value = true
  drawError.value = ''
  drawResult.value = null
  try {
    const result = await lotteryStore.drawWinner(props.lotteryId)
    if (result.success) {
      drawResult.value = result.result
      await lotteryStore.fetchDrawHistory(props.lotteryId)
      return
    }
    drawError.value = parseDrawExecutionError(result.error, 'Failed to execute draw.')
  } catch (err) {
    drawError.value = parseDrawExecutionError(err, 'Failed to execute draw.')
  } finally {
    isDrawing.value = false
  }
}

const openBatchModal = async (draw) => {
  if (!draw?.ticket_batch_id) return
  modalLoading.value = true
  modalBatch.value = null
  modalHighlightTicket.value = draw.winning_number ?? null
  modalUsername.value = formatUserLabel(draw.user_id)
  modalTitle.value = `Vinnerloddet — ${modalUsername.value}`

  const result = await lotteryStore.fetchBatchDetail(props.lotteryId, draw.ticket_batch_id)
  if (result.success) {
    modalBatch.value = result.data
  }
  modalLoading.value = false
}

const closeModal = () => {
  modalBatch.value = null
  modalHighlightTicket.value = null
  modalUsername.value = ''
}
</script>

<template>
  <div class="draw-panel">
    <div class="draw-button-section">
      <button @click="handleDraw" :disabled="drawBlocked" class="draw-btn">
        {{ isDrawing ? '🎲 Drawing...' : '🎲 Draw Winner' }}
      </button>
    </div>

    <p v-if="drawBlocked" class="draw-warning">Draw execution is active. Replay and navigation are temporarily blocked.</p>
    <p v-if="drawError" class="draw-error">{{ drawError }}</p>

    <!-- Siste trekningsresultat -->
    <div v-if="drawResult" class="draw-result">
      <h3>🎉 Draw Result</h3>
      <div class="result-box">
        <table v-if="winnerRows.length" class="history-table">
          <thead>
            <tr>
              <th>Winner</th>
              <th>Ticket</th>
              <th>Batch</th>
              <th>State</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="winner in winnerRows" :key="winner.key" :class="{ 'winner-duplicate': winner.duplicate, 'winner-invalid': winner.invalid }">
              <td>{{ formatWinnerLabel(winner) }}</td>
              <td>{{ formatTicketNumber(winner.ticketNumber) }}</td>
              <td>
                <button
                  v-if="winner.record?.ticket_batch_id"
                  class="batch-link"
                  @click="openBatchModal(winner.record)"
                >
                  🖼 #{{ winner.record.ticket_batch_id }}
                </button>
                <span v-else>–</span>
              </td>
              <td><LifecycleStatusBadge :status-meta="winnerLifecycleMeta(winner)" /></td>
            </tr>
          </tbody>
        </table>
        <p v-else class="draw-warning">Draw finished, but winner records are incomplete. Check execution history.</p>
      </div>
    </div>

    <!-- Historikk -->
    <div v-if="drawHistoryRows.length > 0" class="history-section">
      <h3>Draw History</h3>

      <!-- Mobilvisning -->
      <div v-if="isMobile" class="history-card-list">
        <AppCard
          v-for="draw in drawHistoryRows"
          :key="draw.key"
          class="mobile-card history-card"
          :class="{ clickable: draw.ticketBatchId }"
          :title="draw.ticketBatchId ? 'Klikk for å se vinnerark' : ''"
          @click="openBatchModal(draw.drawRecord)"
        >
          <div class="mobile-card-title-wrap">
            <div class="mobile-card-title">Winning #{{ formatTicketNumber(draw.ticketNumber) }}</div>
            <div class="mobile-card-subtitle">{{ draw.userId !== null ? formatUserLabel(draw.userId) : 'Unknown user' }}</div>
          </div>

          <div class="mobile-card-meta">
            <div><strong>Batch:</strong> {{ draw.ticketBatchId ? `#${draw.ticketBatchId}` : '–' }}</div>
            <div><strong>Created:</strong> {{ draw.createdAt || 'Timestamp unavailable' }}</div>
          </div>

          <div v-if="draw.ticketBatchId" class="mobile-card-actions">
            <button type="button" class="btn btn-primary" @click.stop="openBatchModal(draw.drawRecord)">View vinnerark</button>
          </div>
        </AppCard>
      </div>

      <!-- Skrivebordsvisning -->
      <table v-else class="history-table table-wrapper">
        <thead>
          <tr>
            <th>Winning #</th>
            <th>User</th>
            <th>Batch</th>
            <th>Created</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="draw in drawHistoryRows"
            :key="draw.key"
            :class="{ clickable: draw.ticketBatchId }"
            :title="draw.ticketBatchId ? 'Klikk for å se vinnerark' : ''"
            @click="openBatchModal(draw.drawRecord)"
          >
            <td>{{ formatTicketNumber(draw.ticketNumber) }}</td>
            <td>{{ draw.userId !== null ? formatUserLabel(draw.userId) : 'Unknown user' }}</td>
            <td>
              <span v-if="draw.ticketBatchId" class="batch-chip">
                🖼 #{{ draw.ticketBatchId }}
              </span>
              <span v-else>–</span>
            </td>
            <td>{{ draw.createdAt || 'Timestamp unavailable' }}</td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Laster-overlay -->
    <div v-if="modalLoading" class="modal-overlay-simple">
      <div class="loading-box">Henter vinnerark...</div>
    </div>

    <!-- Batch-modal -->
    <TicketBatchModal
      v-if="modalBatch"
      :batch="modalBatch"
      :highlight-ticket="modalHighlightTicket"
      :username="modalUsername"
      :title="modalTitle"
      @close="closeModal"
    />
  </div>
</template>

<style scoped>
/* --- eksisterende stiler beholdes --- */
.draw-panel { display: grid; gap: 2rem; }
.draw-button-section { display: flex; gap: 1rem; }
.draw-btn {
  padding: 1rem 2rem;
  background-color: #4caf50;
  color: white;
  border: none;
  border-radius: 4px;
  font-weight: bold;
  cursor: pointer;
  font-size: 1rem;
}
.draw-btn:hover:not(:disabled) { background-color: #45a049; }
.draw-btn:disabled { background-color: #ccc; }
.draw-warning {
  color: #92400e;
  background: #fffbeb;
  border: 1px solid #fde68a;
  border-radius: 6px;
  padding: 0.5rem;
}
.draw-error {
  color: #991b1b;
  background: #fee2e2;
  border: 1px solid #fecaca;
  border-radius: 6px;
  padding: 0.5rem;
}
.draw-result {
  padding: 1.5rem;
  background-color: #fff9c4;
  border-left: 4px solid #fbc02d;
  border-radius: 8px;
}
.draw-result h3 { color: #111827; margin: 0 0 1rem 0; }
.result-box {
  background-color: white;
  padding: 1rem;
  border-radius: 4px;
  border: 1px solid #fbc02d;
}
.result-box p { color: #111827; margin: 0.5rem 0; }
.history-section {
  padding: 1rem;
  background-color: white;
  border-radius: 8px;
  border: 1px solid #e0e0e0;
}
.history-section h3 { color: #111827; margin: 0 0 1rem 0; }
.history-table { width: 100%; border-collapse: collapse; }
.history-table th {
  background-color: #f5f5f5;
  color: #111827;
  padding: 0.75rem;
  text-align: left;
  font-weight: bold;
  border-bottom: 1px solid #ddd;
}
.history-table td {
  padding: 0.75rem;
  border-bottom: 1px solid #f0f0f0;
  color: #111827;
}
.history-table tr.clickable { cursor: pointer; }
.history-table tr.clickable:hover { background-color: #eff6ff; }
.winner-duplicate { background: #fff7ed; }
.winner-invalid { background: #fef2f2; }

.history-card-list {
  display: grid;
  gap: 0.75rem;
}

.mobile-card-title-wrap {
  display: grid;
  gap: 0.2rem;
}

.mobile-card-title {
  font-weight: 700;
  color: #111827;
}

.mobile-card-subtitle {
  color: #6b7280;
  font-size: 0.9rem;
}

.mobile-card-meta {
  display: grid;
  gap: 0.35rem;
  color: #111827;
  font-size: 0.95rem;
}

.mobile-card-meta strong {
  color: #374151;
}

.mobile-card-actions {
  display: flex;
  margin-top: 0.5rem;
}

.batch-link {
  background: #eff6ff;
  border: 1px solid #bfdbfe;
  color: #1d4ed8;
  border-radius: 6px;
  padding: 0.3rem 0.7rem;
  cursor: pointer;
  font-size: 0.875rem;
  font-weight: 600;
}
.batch-link:hover { background: #dbeafe; }
.batch-chip {
  background: #eff6ff;
  color: #1d4ed8;
  border-radius: 4px;
  padding: 0.2rem 0.5rem;
  font-size: 0.8rem;
  font-weight: 600;
}
.modal-overlay-simple {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.3);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 999;
}
.loading-box {
  background: white;
  padding: 2rem 3rem;
  border-radius: 8px;
  font-weight: bold;
  color: #2196f3;
}

.table-wrapper {
  overflow-x: auto;
}

.history-card {
  display: grid;
  gap: 8px;
}
</style>

